import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import useSnackbarHelper from '../../../hooks/snackbar.hook';
import useService from '../../../hooks/service.hook';
import TrainingCommentsService from '../../../services/training-comments.service';
import AssignedTrainingsService from '../../../services/assigned-trainings.service';
import { Training, TrainingTeamAssignee } from '../../../models/training.models';
import { TrainingComment, TrainingCommentGroup } from '../../../models/training-comment.models';
import { User } from '../../../models/user.models';
import CoachAthleteTrainingModal from '../../coach-athlete-training-modal';
import CommentCard, { getEffortTierBackgroundColor, getEffortTierTextColor } from './comment-card';
import { palette } from '../../../theme';

export interface CommentsTabProps {
  trainingId: string;
  training?: Training;
  trainingType?: 'athlete' | 'coach' | 'guardian';
  onUnreadCommentsCleared?: (clearedCount: number) => void;
  onTeamUnreadCommentsCleared?: () => void;
  onEffortRatingUpdated?: () => void;
  onRefresh?: () => void;
}

function getCurrentUserName(): string {
  const user: User = JSON.parse(window.localStorage.getItem('user') as string);
  return `${user.firstName} ${user.lastName}`;
}

const CommentsTab: FC<CommentsTabProps> = ({
  trainingId,
  training,
  trainingType,
  onUnreadCommentsCleared,
  onTeamUnreadCommentsCleared,
  onEffortRatingUpdated,
  onRefresh,
}) => {
  const trainingCommentsService = useService(TrainingCommentsService);
  const assignedTrainingsService = useService(AssignedTrainingsService);
  const { showSnackbar } = useSnackbarHelper();

  const [effortRating, setEffortRating] = useState<number | null>(training?.workoutData?.effortRating ?? null);

  useEffect(() => {
    setEffortRating(training?.workoutData?.effortRating ?? null);
  }, [training?.workoutData?.effortRating]);

  const [commentText, setCommentText] = useState<string>('');
  const [commentSubmitting, setCommentSubmitting] = useState<boolean>(false);
  const [commentError, setCommentError] = useState<string>('');
  const [comments, setComments] = useState<TrainingComment[]>([]);
  const [commentGroups, setCommentGroups] = useState<TrainingCommentGroup[]>([]);
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [commentsError, setCommentsError] = useState<string>('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [commentToDelete, setCommentToDelete] = useState<TrainingComment | null>(null);
  const [deletingComment, setDeletingComment] = useState<boolean>(false);

  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [commentMenuId, setCommentMenuId] = useState<number | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentMessage, setEditingCommentMessage] = useState<string>('');

  const [replyingToMemberId, setReplyingToMemberId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState<string>('');
  const [replySubmitting, setReplySubmitting] = useState<boolean>(false);

  const isCoach = trainingType === 'coach';
  const isAthlete = trainingType === 'athlete';
  const isCoachAssigned = !!training?.workoutData;
  const hasTeam = !!training?.team;
  const showSubtabs = isCoach && hasTeam && isCoachAssigned;

  const [activeSubtab, setActiveSubtab] = useState<'personal' | 'team'>('team');

  const isTeamView = isCoach && (activeSubtab === 'team' || !showSubtabs);
  const showEffortRating = !!training?.workoutData && (isAthlete || (isCoach && activeSubtab === 'personal'));

  const [showCoachAthleteModal, setShowCoachAthleteModal] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<TrainingTeamAssignee | null>(null);

  const currentUserName = getCurrentUserName();

  const handleEffortRating = async (rating: number) => {
    if (!trainingId) return;
    try {
      await assignedTrainingsService.updateEffortRating(trainingId, { effortRating: rating });
      setEffortRating(rating);
      showSnackbar('Effort rating updated', 'success');
      onEffortRatingUpdated?.();
    } catch {
      showSnackbar('Failed to update effort rating', 'error');
    }
  };

  const fetchPersonalComments = () => {
    if (!trainingId) return;

    setCommentsLoading(true);
    setCommentsError('');

    trainingCommentsService
      .getTrainingComments(trainingId)
      .then((response) => {
        setComments(response.data.data || []);
        setCommentGroups([]);
        setCommentsLoading(false);
      })
      .catch(() => {
        setCommentsLoading(false);
        setCommentsError('Failed to load comments');
        showSnackbar('There was an error loading comments. Please try again later.', 'error');
      });
  };

  const fetchTeamComments = () => {
    if (!trainingId) return;

    setCommentsLoading(true);
    setCommentsError('');

    trainingCommentsService
      .getTeamTrainingComments(trainingId)
      .then((response) => {
        setCommentGroups(response.data.data || []);
        setComments([]);
        setCommentsLoading(false);
      })
      .catch(() => {
        setCommentsLoading(false);
        setCommentsError('Failed to load comments');
        showSnackbar('There was an error loading comments. Please try again later.', 'error');
      });
  };

  const fetchComments = () => {
    if (isTeamView) {
      fetchTeamComments();
    } else {
      fetchPersonalComments();
    }
  };

  const markPersonalCommentsAsRead = () => {
    if (!trainingId) return;

    const totalUnreadComments = training?.unreadComments || 0;
    const memberUnreadComments =
      training?.team?.assignees?.reduce((total, assignee) => total + (assignee.unreadComments || 0), 0) || 0;
    const generalUnreadComments = Math.max(0, totalUnreadComments - memberUnreadComments);

    trainingCommentsService
      .markCommentsAsRead(trainingId)
      .then(() => {
        onUnreadCommentsCleared?.(generalUnreadComments);
      })
      .catch(() => {
        void 0;
      });
  };

  const markTeamCommentsAsRead = () => {
    if (!trainingId) return;

    trainingCommentsService
      .markTeamCommentsAsRead(trainingId)
      .then(() => {
        onTeamUnreadCommentsCleared?.();
      })
      .catch(() => {
        void 0;
      });
  };

  const markCommentsAsRead = () => {
    if (isTeamView) {
      markTeamCommentsAsRead();
    } else {
      markPersonalCommentsAsRead();
    }
  };

  useEffect(() => {
    if (trainingId) {
      setReplyingToMemberId(null);
      setReplyText('');
      fetchComments();
      markCommentsAsRead();
    }
  }, [trainingId, activeSubtab, isCoach, showSubtabs]);

  const handleRefresh = () => {
    if (!trainingId) return;
    fetchComments();
    markCommentsAsRead();
  };

  const findTeamMemberByMemberId = (memberId: number) => {
    if (!training?.team?.assignees) return null;
    return training.team.assignees.find((assignee) => assignee.memberId === memberId) || null;
  };

  const findTeamMemberByName = (creatorName: string) => {
    if (!training?.team?.assignees) return null;
    return training.team.assignees.find((assignee) => assignee.name === creatorName) || null;
  };

  const findCommentById = (commentId: number): TrainingComment | undefined => {
    const personal = comments.find((c) => c.id === commentId);
    if (personal) return personal;
    for (const group of commentGroups) {
      const groupComment = group.comments.find((c) => c.id === commentId);
      if (groupComment) return groupComment;
    }
    return undefined;
  };

  const handleMemberClick = (member: TrainingTeamAssignee) => {
    if (trainingType === 'coach') {
      setSelectedMember(member);
      setShowCoachAthleteModal(true);
    }
  };

  const handleCommentSubmit = () => {
    if (!trainingId) return;

    const trimmedText = commentText.trim();
    if (!trimmedText) {
      setCommentError('Comment cannot be empty');
      return;
    }

    setCommentSubmitting(true);
    setCommentError('');

    trainingCommentsService
      .createTrainingComment(trainingId, trimmedText)
      .then(() => {
        setCommentSubmitting(false);
        setCommentText('');
        showSnackbar('Comment added successfully', 'success');
        fetchComments();
        markCommentsAsRead();
      })
      .catch(() => {
        setCommentSubmitting(false);
        showSnackbar('There was an error adding your comment. Please try again later.', 'error');
      });
  };

  const handleReplySubmit = (memberId: number) => {
    if (!trainingId) return;

    const trimmedText = replyText.trim();
    if (!trimmedText) return;

    setReplySubmitting(true);

    trainingCommentsService
      .createTrainingComment(trainingId, trimmedText, memberId)
      .then(() => {
        setReplySubmitting(false);
        setReplyText('');
        setReplyingToMemberId(null);
        showSnackbar('Reply added successfully', 'success');
        fetchTeamComments();
      })
      .catch(() => {
        setReplySubmitting(false);
        showSnackbar('There was an error adding your reply. Please try again later.', 'error');
      });
  };

  const handleCommentTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(event.target.value);
    if (commentError) {
      setCommentError('');
    }
  };

  const handleCommentMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: number) => {
    event.stopPropagation();
    setCommentMenuAnchorEl(event.currentTarget);
    setCommentMenuId(commentId);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchorEl(null);
    setCommentMenuId(null);
  };

  const handleEditComment = (commentId: number) => {
    handleCommentMenuClose();
    const comment = findCommentById(commentId);
    if (comment) {
      setTimeout(() => {
        setEditingCommentId(commentId);
        setEditingCommentMessage(comment.message);
      }, 0);
    }
  };

  const handleCancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentMessage('');
  };

  const handleSaveComment = async (commentId: number) => {
    if (!trainingId) return;
    const trimmedMessage = editingCommentMessage.trim();
    const comment = findCommentById(commentId);

    if (!comment) return;

    if (trimmedMessage === comment.message.trim()) {
      handleCancelCommentEdit();
      return;
    }

    try {
      const res = await trainingCommentsService.updateTrainingComment(trainingId, commentId, trimmedMessage);
      const updatedAt = res.data.data?.updated_at || new Date().toISOString();

      const updateComment = (c: TrainingComment) =>
        c.id === commentId ? { ...c, message: trimmedMessage, updated_at: updatedAt } : c;

      setComments((prev) => prev.map(updateComment));
      setCommentGroups((prev) =>
        prev.map((group) => ({
          ...group,
          comments: group.comments.map(updateComment),
        }))
      );
      showSnackbar('Comment updated', 'success');
      handleCancelCommentEdit();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to update comment. Please try again.';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLElement).blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelCommentEdit();
    }
  };

  const handleDeleteComment = (comment: TrainingComment) => {
    handleCommentMenuClose();
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteComment = () => {
    if (!commentToDelete || !trainingId) return;

    setDeletingComment(true);

    trainingCommentsService
      .deleteTrainingComment(trainingId, commentToDelete.id)
      .then(() => {
        setDeletingComment(false);
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
        showSnackbar('Comment deleted successfully', 'success');
        fetchComments();
      })
      .catch(() => {
        setDeletingComment(false);
        showSnackbar('There was an error deleting your comment. Please try again later.', 'error');
      });
  };

  const cancelDeleteComment = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const showAddComment = !isCoach || (isCoach && activeSubtab === 'personal');

  const sharedCardProps = {
    commentMenuAnchorEl,
    commentMenuId,
    onCommentMenuOpen: handleCommentMenuOpen,
    onCommentMenuClose: handleCommentMenuClose,
    onEditComment: handleEditComment,
    onDeleteComment: handleDeleteComment,
    editingCommentId,
    editingCommentMessage,
    onEditingMessageChange: setEditingCommentMessage,
    onSaveComment: handleSaveComment,
    onCommentKeyDown: handleCommentKeyDown,
  };

  const renderReplyInput = (memberId: number) => (
    <Box sx={{ ml: 5, mt: 1 }}>
      <TextField
        multiline
        minRows={2}
        fullWidth
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        placeholder="Write your reply..."
        variant="outlined"
        disabled={replySubmitting}
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            fontSize: 13,
            borderRadius: 2,
            backgroundColor: palette.background.paper,
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={() => {
            setReplyingToMemberId(null);
            setReplyText('');
          }}
          disabled={replySubmitting}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => handleReplySubmit(memberId)}
          disabled={replySubmitting || !replyText.trim()}
          sx={{ textTransform: 'none', borderRadius: 2, minWidth: 72 }}
        >
          {replySubmitting ? <CircularProgress size={16} color="inherit" /> : 'Send'}
        </Button>
      </Box>
    </Box>
  );

  const renderTeamCommentGroups = () =>
    commentGroups.map((group) => {
      const assignee = findTeamMemberByMemberId(group.member_id);
      const parentComment = group.comments[0];
      const threadComments = group.comments.slice(1);

      if (!parentComment) return null;

      return (
        <Box key={group.member_id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <CommentCard
            variant="parent"
            comment={parentComment}
            headerName={group.name}
            assignee={assignee}
            showEffortBadge
            showMemberLink={!!assignee}
            onMemberClick={assignee ? () => handleMemberClick(assignee) : undefined}
            showReplyButton={isCoach && threadComments.length === 0}
            onReply={() => {
              setReplyingToMemberId(group.member_id);
              setReplyText('');
            }}
            canShowEditMenu={parentComment.can_edit}
            {...sharedCardProps}
          />

          {threadComments.map((comment) => {
            const isCoachReply = comment.creator === currentUserName;
            return (
              <CommentCard
                key={comment.id}
                variant="thread"
                comment={comment}
                headerName={isCoachReply ? undefined : comment.creator}
                assignee={isCoachReply ? undefined : findTeamMemberByName(comment.creator)}
                isCoachReply={isCoachReply}
                canShowEditMenu={comment.can_edit}
                {...sharedCardProps}
              />
            );
          })}

          {threadComments.length > 0 && isCoach && (
            <Button
              size="small"
              onClick={() => {
                setReplyingToMemberId(group.member_id);
                setReplyText('');
              }}
              sx={{
                mt: 0.5,
                ml: 5,
                alignSelf: 'flex-start',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 500,
                color: palette.primary.main,
                border: `1px solid ${palette.primary.light}`,
                borderRadius: 2,
                px: 1.5,
                py: 0.25,
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
              }}
            >
              Reply
            </Button>
          )}

          {replyingToMemberId === group.member_id && renderReplyInput(group.member_id)}
        </Box>
      );
    });

  const renderPersonalComments = () =>
    comments.map((comment) => {
      const teamMember = findTeamMemberByName(comment.creator);

      return (
        <CommentCard
          key={comment.id}
          variant="parent"
          comment={comment}
          assignee={teamMember}
          showMemberLink={!!teamMember && isCoach}
          onMemberClick={teamMember ? () => handleMemberClick(teamMember) : undefined}
          canShowEditMenu={comment.can_edit && showAddComment}
          {...sharedCardProps}
        />
      );
    });

  const isEmpty = isTeamView ? commentGroups.length === 0 : comments.length === 0;

  return (
    <Box sx={{ padding: '16px', minHeight: 'fit-content', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          onClick={handleRefresh}
          disabled={commentsLoading}
          size="small"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
          }}
          aria-label="Refresh comments"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {showSubtabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
          <Tabs
            value={activeSubtab === 'personal' ? 0 : 1}
            onChange={(_, newValue) => setActiveSubtab(newValue === 0 ? 'personal' : 'team')}
            centered
            sx={{
              minHeight: 40,
              '.MuiTab-root': { fontSize: 14, fontWeight: 500, textTransform: 'none', color: palette.text.secondary },
              '.Mui-selected': { color: palette.primary.main, fontWeight: 600 },
              '.MuiTabs-indicator': { backgroundColor: palette.primary.main, height: 3 },
            }}
          >
            <Tab label="Personal View" />
            <Tab label="Team View" />
          </Tabs>
        </Box>
      )}

      {showEffortRating && (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              fontSize: 11,
              fontWeight: 600,
              color: palette.text.secondary,
              mb: 1.5,
            }}
          >
            Perceived Effort
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
              const isSelected = effortRating === n;
              const bgColor = getEffortTierBackgroundColor(n);
              const textColor = getEffortTierTextColor(n);
              return (
                <Button
                  key={n}
                  onClick={() => handleEffortRating(n)}
                  size="small"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: 13,
                    p: 0,
                    backgroundColor: isSelected ? bgColor : 'transparent',
                    color: isSelected ? textColor : palette.text.secondary,
                    border: `1px solid ${isSelected ? bgColor : palette.grey[300]}`,
                    outline: isSelected ? `2px solid ${bgColor}` : 'none',
                    outlineOffset: '1px',
                    '&:hover': {
                      backgroundColor: bgColor,
                      color: textColor,
                      borderColor: bgColor,
                    },
                  }}
                >
                  {n}
                </Button>
              );
            })}
          </Box>
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      {isCoach &&
        activeSubtab === 'team' &&
        (() => {
          const assignees = training?.team?.assignees ?? [];
          const ratedAssignees = assignees.filter((a) => a.effortRating != null);
          const avg =
            ratedAssignees.length > 0
              ? ratedAssignees.reduce((sum, a) => sum + (a.effortRating ?? 0), 0) / ratedAssignees.length
              : null;
          const countByRating = Array.from({ length: 10 }, (_, i) => ({
            rating: i + 1,
            count: ratedAssignees.filter((a) => a.effortRating === i + 1).length,
          }));

          return (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: palette.text.secondary,
                  }}
                >
                  Perceived Effort
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: palette.text.secondary }}>
                  {avg !== null ? `Avg ${avg.toFixed(1)}` : 'No ratings yet'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {countByRating.map(({ rating, count }) => {
                  const bgColor = getEffortTierBackgroundColor(rating);
                  const textColor = getEffortTierTextColor(rating);
                  const hasVotes = count > 0;
                  return (
                    <Box key={rating} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          fontWeight: 700,
                          fontSize: 12,
                          backgroundColor: hasVotes ? bgColor : 'transparent',
                          color: hasVotes ? textColor : palette.grey[400],
                          border: `1px solid ${hasVotes ? bgColor : palette.grey[200]}`,
                        }}
                      >
                        {rating}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: hasVotes ? 700 : 400,
                          color: hasVotes ? palette.text.primary : palette.grey[400],
                          mt: 0.5,
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })()}

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {commentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <CircularProgress size={30} />
          </Box>
        ) : commentsError ? (
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {commentsError}
          </Typography>
        ) : isEmpty ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: palette.grey[300] }} />
            <Typography variant="body2" color={palette.text.disabled}>
              No notes yet. Be the first to add one!
            </Typography>
          </Box>
        ) : isTeamView ? (
          renderTeamCommentGroups()
        ) : (
          renderPersonalComments()
        )}
      </Box>

      {showAddComment && (
        <Box sx={{ borderTop: `1px solid ${palette.divider}`, pt: 2, mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text.secondary, mb: 1.5 }}>
            Add a Comment
          </Typography>

          <TextField
            multiline
            rows={3}
            fullWidth
            value={commentText}
            onChange={handleCommentTextChange}
            placeholder="Write your comment here..."
            variant="outlined"
            error={!!commentError}
            helperText={commentError}
            disabled={commentSubmitting}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: 14,
                borderRadius: 2,
                backgroundColor: palette.background.default,
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCommentSubmit}
              disabled={commentSubmitting || !commentText.trim()}
              sx={{ textTransform: 'none', minWidth: 100, borderRadius: 2 }}
            >
              {commentSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Post Comment'}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteComment}
        aria-labelledby="delete-comment-dialog-title"
        aria-describedby="delete-comment-dialog-description"
      >
        <DialogTitle id="delete-comment-dialog-title">Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-comment-dialog-description">
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteComment} disabled={deletingComment}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteComment}
            color="error"
            variant="contained"
            disabled={deletingComment}
            startIcon={deletingComment ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingComment ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedMember && training?.team && (
        <CoachAthleteTrainingModal
          open={showCoachAthleteModal}
          onClose={() => {
            setShowCoachAthleteModal(false);
            setSelectedMember(null);
          }}
          teamId={String(training.team.id)}
          memberId={selectedMember.memberId}
          trainingId={trainingId}
          memberPreview={{
            name: selectedMember.name,
            profileImage: selectedMember.profileImage,
          }}
          onTrainingUpdated={() => {
            onRefresh?.();
          }}
        />
      )}
    </Box>
  );
};

export default CommentsTab;
